import { ChangeDetectionStrategy, Component, OnInit, computed, inject } from '@angular/core';
import {
  InfiniteScrollCustomEvent,
  IonButton,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
} from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EmptyResultComponent,
  InfiniteScrollHeaderComponent,
  SkeletonItemTwoColumnsComponent,
  SpinnerLoadingComponent,
  type IInfiniteScrollHeaderConfig,
} from '@okcargo-apps/layout-ionic';
import { MessageUiIonicComponent, MessagesUiService } from '@okcargo-apps/messages-ui';
import { VehicleItemComponent } from '../vehicle-item/vehicle-item.component';
import { VEHICLES_LIST_MESSAGE_OBJECT_ID } from '../vehicles-store.model';
import { VehiclesStoreService } from '../vehicles-store.service';

@Component({
  selector: 'lib-vehicle-list',
  templateUrl: './vehicle-list.component.html',
  styleUrls: ['./vehicle-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonButton,
    IonList,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    InfiniteScrollHeaderComponent,
    SpinnerLoadingComponent,
    MessageUiIonicComponent,
    TranslateModule,
    VehicleItemComponent,
    SkeletonItemTwoColumnsComponent,
    EmptyResultComponent,
  ],
})
export class VehicleListComponent implements OnInit {
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly vehiclesStoreService = inject(VehiclesStoreService);

  public readonly vehiclesVM = this.vehiclesStoreService.vehiclesVM;

  // The vehicles are not refreshed by the general application refresh: each component that
  // consumes them reloads the data when it is initialized (see VehiclesStoreService).
  public ngOnInit(): void {
    this.vehiclesStoreService.reloadVehiclesOnComponentInit();
  }

  public readonly messages = computed(() =>
    this.messagesUiService.getMessagesForObjectId(VEHICLES_LIST_MESSAGE_OBJECT_ID),
  );

  protected readonly headerConfig = computed<IInfiniteScrollHeaderConfig>(() => {
    const vm = this.vehiclesVM();

    return {
      text: this.translate.instant('general.item_count', { count: vm.totalElements }),
      lastUpdated: vm.lastLoadedAt,
      staleMessage: vm.isStale ? this.translate.instant('general.stale_data') : undefined,
      refreshButton: vm.isStale
        ? {
            label: this.translate.instant('general.refresh'),
            action: () => this.vehiclesStoreService.reloadVehicles('user'),
          }
        : undefined,
    };
  });

  protected onIonInfinite(event: InfiniteScrollCustomEvent): void {
    this.vehiclesStoreService.loadNextVehiclesPage();
    event.target.complete();
  }

  protected onRetry(): void {
    this.vehiclesStoreService.retryVehicles();
  }
}
